import crownGold from "assets/images/crownGold.svg"
import { useGraphqlQuery } from "graphql-mock"
import { Card, IconCertified } from "materia"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import { FiestaLink } from "utility-components"
import { displayTimestamp, exists } from "utils"
import { Avatar } from "../../../components/Avatar"
import { WidgetLink } from "../../../components/Widget"
import { useGetOrdinal } from "../../../dataUtilities/getOrdinal"
import { query, type RecentActivityEvent } from "./gql"
import { RecentActivityWidgetMobileError } from "./RecentActivityWidgetMobileError"
import { RecentActivityWidgetMobileSkeleton } from "./RecentActivityWidgetMobileSkeleton"

import { Stars } from "../../../components/Stars"
import s from "./RecentActivityWidgetMobile.module.scss"

export const RecentActivityWidgetMobile = () => {
  const { data, error, isPending } = useGraphqlQuery(query, {})

  const events = [data?.completed, data?.leaderboard, data?.stars]
    .map((data) => data?.nodes[0])
    .filter(exists)

  const activeEntry = useActiveEntry(events.length)

  if (isPending) return <RecentActivityWidgetMobileSkeleton />
  if (error) return <RecentActivityWidgetMobileError error={error} />

  const { currentCompany } = data
  const { portalFeatureFlags } = currentCompany

  if (!portalFeatureFlags.includes("enable_activity_feed")) return
  if (events.length === 0) return

  return (
    <Card>
      <div className={s.widget}>
        {events.map((event, index) => (
          <Entry key={index} event={event} isActive={activeEntry === index} />
        ))}
      </div>
    </Card>
  )
}

const Entry = ({
  event,
  isActive,
}: {
  event: RecentActivityEvent
  isActive: boolean
}) => {
  const { t, i18n } = useTranslation()
  const getOrdinal = useGetOrdinal()

  const { user, product, createdAt, eventType, rank, stars } = event

  const getSummaryText = () => {
    if (eventType === "stars" && stars)
      return t("activityFeed.stars", { count: stars })
    if (eventType === "leaderboard" && rank) return getOrdinal(rank)
    return t("activityFeed.completed")
  }

  const entryClass = modifiers(s, "entry", {
    orange: eventType === "leaderboard",
    green: eventType === "completed",
    isActive,
  })

  const getIcon = () => {
    if (eventType === "completed") {
      return <IconCertified size="small" />
    }
    if (eventType === "stars") {
      return <Stars count={1} max={1} size="extraSmall" />
    }
    if (eventType === "leaderboard") {
      return <img className={s.entry__icon} src={crownGold} alt="" />
    }
  }

  return (
    <FiestaLink to="recent-activity-widget" className={entryClass}>
      <span className={s.entry__avatar}>
        <Avatar alt={user.avatarTitle} image={user.avatar} size="small" />
      </span>
      <div className={s.entry__userSummary}>
        <div className={s.entry__summaryTop}>
          <div className={s.entry__name}>{user.displayName}</div>
          <div className={s.entry__time}>
            {displayTimestamp(createdAt, t, i18n.language)}
          </div>
        </div>
        <div className={s.entry__summaryBottom}>
          {getIcon()}
          <div className={s.entry__summaryText}>{getSummaryText()}</div>
          <div className={s.entry__productTitle}>{product.title}</div>
        </div>
      </div>

      <WidgetLink
        to="recent-activity-widget"
        ariaLabel="Open recent activity widget"
      />
    </FiestaLink>
  )
}

const useActiveEntry = (numberOfEntries: number) => {
  const [activeEntry, setActiveEntry] = useState(0)

  useEffect(() => {
    const onInterval = () => {
      setActiveEntry((activeEntry) => {
        if (activeEntry + 1 >= numberOfEntries) return 0
        return activeEntry + 1
      })
    }
    const interval = window.setInterval(onInterval, 6000)

    return () => window.clearInterval(interval)
  }, [numberOfEntries])

  return activeEntry
}
